import { CalendarX } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SlotCard } from '@/components/booking/SlotCard';

import type { BookingSlot } from '@/types/scheduling.types';

interface SlotListProps {
  slots: BookingSlot[];
  timezone: string;
  selectedSlotId: string | null;
  onSelectSlot: (slotId: string) => void;
}

function formatGroupDate(utcString: string, timezone: string): string {
  return new Date(utcString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  });
}

function getDateKey(utcString: string, timezone: string): string {
  return new Date(utcString).toLocaleDateString('en-US', { timeZone: timezone });
}

interface SlotGroup {
  dateKey: string;
  label: string;
  slots: BookingSlot[];
}

export function SlotList({ slots, timezone, selectedSlotId, onSelectSlot }: SlotListProps) {
  const { t } = useTranslation();

  const groups = useMemo(() => {
    const groupMap = new Map<string, SlotGroup>();

    const sorted = [...slots].sort((a, b) => new Date(a.startAtUtc).getTime() - new Date(b.startAtUtc).getTime());

    for (const slot of sorted) {
      const dateKey = getDateKey(slot.startAtUtc, timezone);
      const existing = groupMap.get(dateKey);
      if (existing) {
        existing.slots.push(slot);
      } else {
        groupMap.set(dateKey, {
          dateKey,
          label: formatGroupDate(slot.startAtUtc, timezone),
          slots: [slot],
        });
      }
    }

    return Array.from(groupMap.values());
  }, [slots, timezone]);

  if (slots.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-12 text-center'>
        <CalendarX className='h-12 w-12 text-gray-300' />
        <p className='mt-3 text-sm text-gray-500'>{t('booking.noSlots')}</p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {groups.map((group) => (
        <div key={group.dateKey}>
          <h3 className='mb-3 text-sm font-semibold text-gray-700'>{group.label}</h3>
          <div className='space-y-2'>
            {group.slots.map((slot) => (
              <SlotCard
                key={slot.slotId}
                slot={slot}
                timezone={timezone}
                isSelected={selectedSlotId === slot.slotId}
                onSelect={onSelectSlot}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
